import React, { useEffect, useRef } from 'react'
import { AuthenticatedHeader } from '../../components/Header/AuthenticatedHeader'
import classNames from 'classnames'
import gridClasses from '../../styles/grid.module.css'
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-right.svg'
import Carousel from '../../components/Carousel/Carousel'
import { ProgressSummaryCard } from '../../components/Cards/ProgressSummaryCard/ProgressSummaryCard'
import Footer from '../../components/Footer/Footer'
import heroBackgroundImage from '../../assets/images/subscriber-home-background-new.jpg'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userSelectors } from '../../redux/userSlice'
import {
    getCourseProgress,
    getOurCourses,
    getRecommendedCourses,
    getUpcomingClasses,
} from './dashboardApi'
import { dashboardSelectors } from './dashboardSlice'
import CardLoader, {
    ProgressLoaderRow,
} from '../../components/loaders/CardLoader'
import { getImageURL } from '../../util/functions'
import coursePlaceholder from '../../assets/images/course-placeholder.svg'
import OurCourses from '../../components/OurCourses/OurCourses'
import OurClasses from '../../components/OurClasses/OurClasses'
import classes from './PersonalUserDashboard.module.css'
import { CourseCard } from '../../components/Cards/GeneralCard/Card'
import { AccountTypes } from '../../util/constants'

const PersonalDashboard = () => {
    const progressPrevButton = useRef()
    const progressNextButton = useRef()

    const recommendedCoursesPrevButton = useRef()
    const recommendedCoursesNextButton = useRef()

    const dispatch = useDispatch()

    const user = useSelector(userSelectors.userDetails)
    const isSubscriber =
        user.accounttype?.toLowerCase() ===
        AccountTypes.Subscriber?.toLowerCase()

    useEffect(() => {
        dispatch(getRecommendedCourses(user.email))
        dispatch(getOurCourses())
        dispatch(getUpcomingClasses())
        dispatch(getCourseProgress())
    }, [])

    const recommended = useSelector(dashboardSelectors.recommended)
    const courses = useSelector(dashboardSelectors.courses)
    const classesArray = useSelector(dashboardSelectors.classes)
    const userCourses = useSelector(dashboardSelectors.userCourses)
    const isFetching = useSelector(dashboardSelectors.isFetching)

    return (
        <>
            <div className={classes.topWrapper}>
                <AuthenticatedHeader isBusinessOwner={true} />
                <div className={classes.top}>
                    <img
                        src={heroBackgroundImage}
                        alt=""
                        className={classes.topBgImage}
                    />
                    <div
                        className={classNames(
                            gridClasses.container,
                            gridClasses.containerTransparent
                        )}
                    >
                        <h1 className={classes.topHeader}>
                            Learn on the
                            <span className={classes.topHeaderOrange}>
                                {' '}
                                job
                            </span>
                        </h1>
                        <h3 className={classes.topSubtext}>
                            You don't have to wait to implement what you've
                            learnt. Our courses are designed to be implemented
                            immediately, right there as you learn. And with the
                            right support from our team of experts, as well as a
                            wide variety of resources, you are assured of the
                            best!
                        </h3>
                    </div>
                </div>
            </div>
            <div
                className={classNames(gridClasses.container, classes.continue)}
            >
                <div className={classes.continueRectangle} />
                {isSubscriber && user.freeCourses > 0 && (
                    <div className={classes.freeCourses}>
                        <p className={classes.freeCoursesText}>
                            You have {user.freeCourses} free course for this
                            month
                        </p>
                        <Link
                            to={'/app/browse/courses'}
                            className={classes.freeCoursesLink}
                        >
                            Browse Courses
                        </Link>
                    </div>
                )}
                <div className={classes.continueText}>
                    <h1 className={classes.continueHeader}>
                        Welcome {user.firstname}
                    </h1>
                    <h3 className={classes.continueSubtext}>
                        Continue from where you left off
                    </h3>
                </div>
                {//Show courses only if the user's courses are still fetching or the user actually has courses
                isFetching.userCourses ||
                    (userCourses.length > 0 && (
                        <div>
                            <button
                                className={classNames(
                                    classes.cardsButton,
                                    classes.cardsButtonLeft
                                )}
                                ref={progressPrevButton}
                            >
                                <ArrowIcon
                                    className={classNames(
                                        classes.cardsButtonIcon,
                                        classes.cardsButtonIconLeft
                                    )}
                                />
                            </button>
                            {isFetching.userCourses ? (
                                <ProgressLoaderRow />
                            ) : (
                                <Carousel
                                    prevButton={progressPrevButton}
                                    nextButton={progressNextButton}
                                    slideClassName={classes.progressCard}
                                >
                                    {userCourses.map(
                                        ({
                                            catName,
                                            title,
                                            progressDetails: {
                                                percentageCompleted = 0,
                                            },
                                            thumbnail,
                                            coursecode,
                                            currentTopicId,
                                            currentLessonId,
                                            classid,
                                        }) => (
                                            <ProgressSummaryCard
                                                category={catName}
                                                title={title}
                                                progress={percentageCompleted}
                                                image={
                                                    thumbnail
                                                        ? getImageURL(thumbnail)
                                                        : coursePlaceholder
                                                }
                                                key={coursecode}
                                                to={`courses/${coursecode}/${classid}/lessons/${currentLessonId}/${currentTopicId?.topicId ||
                                                    1}`}
                                            />
                                        )
                                    )}
                                </Carousel>
                            )}
                            <button
                                className={classNames(
                                    classes.cardsButton,
                                    classes.cardsButtonRight
                                )}
                                ref={progressNextButton}
                            >
                                <ArrowIcon
                                    className={classes.cardsButtonIcon}
                                />
                            </button>
                        </div>
                    ))}
                <div className={classNames(classes.recommendations)}>
                    <section className={classes.section}>
                        <h3
                            className={classes.sectionHeader}
                            style={{ marginBottom: 0 }}
                        >
                            Recommended Courses
                        </h3>
                        <p style={{ marginBottom: 40, marginTop: 10 }}>
                            Browse through our list of recommended courses here.
                        </p>
                        <div>
                            <button
                                className={classNames(
                                    classes.cardsButton,
                                    classes.cardsButtonLeft
                                )}
                                ref={recommendedCoursesPrevButton}
                            >
                                <ArrowIcon
                                    className={classNames(
                                        classes.cardsButtonIcon,
                                        classes.cardsButtonIconLeft
                                    )}
                                />
                            </button>
                            {isFetching.recommended ? (
                                <div
                                    className={classes.recommendedCoursesLoader}
                                >
                                    <CardLoader
                                        className={
                                            classes.recommendedCourseCard
                                        }
                                    />
                                    <CardLoader
                                        className={
                                            classes.recommendedCourseCard
                                        }
                                    />
                                    <CardLoader
                                        className={
                                            classes.recommendedCourseCard
                                        }
                                    />
                                </div>
                            ) : (
                                <Carousel
                                    prevButton={recommendedCoursesPrevButton}
                                    nextButton={recommendedCoursesNextButton}
                                    slideClassName={
                                        classes.recommendedCourseCard
                                    }
                                >
                                    {recommended.map(
                                        ({
                                            title,
                                            thumbnail,
                                            catName,
                                            price,
                                            enrolled,
                                            freeonsubscription,
                                            showstudentcount,
                                            coursecode,
                                        }) => (
                                            <CourseCard
                                                showStudentCount={
                                                    String(showstudentcount) ===
                                                    '1'
                                                }
                                                premium={
                                                    String(
                                                        freeonsubscription
                                                    ) === '0'
                                                }
                                                attendees={enrolled}
                                                coursecode={coursecode}
                                                title={title}
                                                category={catName}
                                                price={price}
                                                url={
                                                    thumbnail
                                                        ? getImageURL(thumbnail)
                                                        : coursePlaceholder
                                                }
                                            />
                                        )
                                    )}
                                </Carousel>
                            )}
                            <button
                                className={classNames(
                                    classes.cardsButton,
                                    classes.cardsButtonRight
                                )}
                                ref={recommendedCoursesNextButton}
                            >
                                <ArrowIcon
                                    className={classes.cardsButtonIcon}
                                />
                            </button>
                        </div>
                    </section>
                    <section className={classes.section}>
                        <h3
                            className={classes.sectionHeader}
                            style={{ marginBottom: 0 }}
                        >
                            Our courses
                        </h3>
                        <p style={{ margin: `10px 0px 40px` }}>
                            Select and implement any course of your choice from
                            our extensive catalogue.
                        </p>
                        <OurCourses />
                    </section>
                    <section className={classes.section}>
                        <h3
                            className={classes.sectionHeader}
                            style={{ marginBottom: 0 }}
                        >
                            Upcoming classes
                        </h3>
                        <p style={{ margin: `10px 0px 40px` }}>
                            Sign up for any of our upcoming classes on your
                            favourite topics. Save the date!
                        </p>
                        <OurClasses />
                    </section>
                </div>
            </div>
            <Footer />
        </>
    )
}

export default PersonalDashboard
